import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { EMPTY, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService } from '@core/services/auth.services';
import { SessionInitializationService } from '@core/services/session-initialization.service';

/** How often the minutes left are recomputed; the session counts in whole minutes. */
const TICK_MS = 15_000;
/** From this many minutes left the banner warns that the session is about to end. */
const WARN_MINUTES = 5;
const MS_PER_MINUTE = 60_000;

/** Top-bar banner of a "Sign in as" tab (US 3635). The words carry the meaning; the colour only adds to it. */
@Component({
  selector: 'app-sign-in-as-banner',
  standalone: true,
  imports: [ButtonModule],
  templateUrl: './sign-in-as-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignInAsBannerComponent {
  private readonly authService = inject(AuthService);
  private readonly sessionInitService = inject(SessionInitializationService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly now = signal(Date.now());
  protected readonly ending = signal(false);

  protected readonly actingUser = this.authService.actingUser;
  protected readonly userName = this.authService.userFullName;
  protected readonly minutesLeft = computed(() => {
    const expiresAt = this.authService.sessionExpiresAt();
    return expiresAt ? Math.max(0, Math.ceil((expiresAt - this.now()) / MS_PER_MINUTE)) : 0;
  });
  protected readonly endingSoon = computed(() => this.minutesLeft() <= WARN_MINUTES);

  constructor() {
    // The banner sits in every top bar; it only ticks while this tab is signed in as someone.
    toObservable(this.authService.isSignedInAs)
      .pipe(
        switchMap((signedInAs) => (signedInAs ? interval(TICK_MS) : EMPTY)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.now.set(Date.now());
        if (this.minutesLeft() === 0) {
          this.sessionInitService.endSignInAs('expired');
        }
      });
  }

  protected endSession(): void {
    this.ending.set(true);
    this.sessionInitService.endSignInAs('ended').finally(() => this.ending.set(false));
  }
}
